import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { authService } from '../../services/authService';
import './AccountModals.css';

const ChangePhoneModal = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState(1); // 1: enter phone, 2: verify code
  const [formData, setFormData] = useState({
    newPhone: '',
    password: '',
    verificationCode: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    setError('');

    if (!/^\+\d+$/.test(formData.newPhone)) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    try {
      await authService.sendPhoneChangeVerification(formData.newPhone);
      setStep(2);
      startCountdown();
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setError('');
    setIsLoading(true);
    try {
      await authService.sendPhoneChangeVerification(formData.newPhone);
      startCountdown();
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndUpdate = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        newPhone: formData.newPhone,
        password: formData.password,
        verificationCode: formData.verificationCode
      });
      handleClose();
    } catch (error) {
      if (error.detail?.includes('Invalid verification code')) {
        setError('Invalid verification code. Please check and try again.');
      } else {
        setError(error.detail || 'Failed to update phone number');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      newPhone: `+${value}`
    });
  };

  const handleBack = () => {
    setStep(1);
    setFormData({ ...formData, verificationCode: '' });
    setError('');
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ newPhone: '', password: '', verificationCode: '' });
    setError('');
    setCountdown(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {step === 1 ? (
          <>
            <h2>Change Phone Number</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSendVerification}>
              <div className="phone-input-group">
                <div className="phone-label">📞 New Phone Number</div>
                <PhoneInput
                  country={'us'}
                  value={formData.newPhone.replace('+', '')}
                  onChange={handlePhoneChange}
                  disabled={isLoading}
                  enableSearch={true}
                  disableSearchIcon={true}
                  disableDropdown={false}
                  searchPlaceholder="Search countries..."
                  inputProps={{
                    required: true,
                    name: 'newPhone'
                  }}
                />
              </div>
              <input
                type="password"
                name="password"
                placeholder="Current Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                disabled={isLoading}
              />
              <div className="modal-buttons">
                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Verification Code'}
                </button>
                <button type="button" onClick={handleClose} className="cancel-button" disabled={isLoading}>
                  Cancel
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2>Verify New Number</h2>
            <p className="verification-info">
              We sent a 6-digit code to<br />
              <strong>{formData.newPhone}</strong>
            </p>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleVerifyAndUpdate}>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={formData.verificationCode}
                onChange={(e) => setFormData({
                  ...formData, 
                  verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                })}
                required
                disabled={isLoading}
                className="verification-input"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <div className="modal-buttons">
                <button type="submit" disabled={isLoading || formData.verificationCode.length !== 6}>
                  {isLoading ? 'Verifying...' : 'Update Phone Number'}
                </button>
                <button type="button" onClick={handleBack} className="cancel-button" disabled={isLoading}>
                  Back
                </button>
              </div>
            </form>
            <div className="verification-actions">
              <button 
                className="link-button" 
                onClick={handleResendCode}
                disabled={countdown > 0 || isLoading}
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangePhoneModal;
